import React, { useEffect, useState } from 'react';
import { ISettingsPopUpParams } from "./ISettingsPopUpParams";
import './SettingsPopUp.css';
import { loginUser, logoutUser, loadConfig, saveStorage, getDefaultAppConfig } from '../../utils/IConfigUtils';
import { IConfig } from "../../utils/IConfig";
import { User } from '../User';
import { convertFileToBase64 } from '../../utils/imgUtils';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { verifyLicense } from '../../utils/licenseUtils';

const emptyUser = User.create(false);
let checkUserInterval: any;
const config: IConfig = loadConfig();

export function SettingsPopUp(params: ISettingsPopUpParams) {
    const { t } = useTranslation();
    const [state, setState] = useState({ 
        username: "", 
        password: "", 
        userLoggedIn: emptyUser, 
        actionStatus: "", 
        checkUserInterval: checkUserInterval, 
        tempConfig: {...config.application},
        generatedActivationFileName: "",
        generatedActivationFileHref: "",
        pastedToken: ""
    });
    const [isFileLoaded, setIsFileLoaded] = useState(false);
    const [licenseFileGenerateFile, setLicenseFileGenerateFile] = useState<any>();

    const urlQueryParams = new URLSearchParams(window.location.search);
    const configParam = urlQueryParams.get('config') || 'default';
    
    useEffect(() => {
        const fetchData = async () => {
          const localStorageKey = `config_${configParam}`;

          if (configParam) {
            try {
              const response = await fetch(`./configs/${configParam}/configuration.json`);
              const jsonData = await response.json();

              if (jsonData.url) {
                 window.location.href = jsonData.url;
                 return;
              }
      
              if (jsonData.configDate !== config.application.configDate || !localStorage.getItem(localStorageKey)) {
                let startLogoLeft: any = config.application.startLogoLeft.src;
                let startLogoRight: any = config.application.startLogoRight.src;
                let artworkLogoLeft: any = config.application.artworkLogoLeft.src;
                let artworkLogoRight: any = config.application.artworkLogoRight.src;

                if (jsonData.startLogoLeft && jsonData.startLogoLeft.name) {
                  startLogoLeft = `./configs/${configParam}/${jsonData.startLogoLeft.name}`;
                }
                if (jsonData.startLogoRight && jsonData.startLogoRight.name) {
                  startLogoRight = `./configs/${configParam}/${jsonData.startLogoRight.name}`;
                }
                if (jsonData.artworkLogoLeft && jsonData.artworkLogoLeft.name) {
                  artworkLogoLeft = `./configs/${configParam}/${jsonData.artworkLogoLeft.name}`;
                }
                if (jsonData.artworkLogoRight && jsonData.artworkLogoRight.name) {
                  artworkLogoRight = `./configs/${configParam}/${jsonData.artworkLogoRight.name}`;
                }
      
                const updatedConfig = {
                  ...config.application,
                  configDate: jsonData.configDate,
                  folderName: jsonData.folderName,
                  timer: jsonData.timer,
                  stripesWidths: jsonData.stripesWidths,
                  maxShapeRotationDegree: jsonData.maxShapeRotationDegree,
                  stripesWidth: jsonData.stripesWidth,
                  selectedLanguage: jsonData.selectedLanguage,
                  shapeRotationDegree: jsonData.shapeRotationDegree,
                  hideIcon: jsonData.hideIcon,
                  logoPdfFileName: jsonData.logoPdfFileName,
                  startLogoLeft: { ...jsonData.startLogoLeft, src: startLogoLeft, name: jsonData.logoLeftFileName || jsonData.startLogoLeft?.name },
                  startLogoRight: { ...jsonData.startLogoRight, src: startLogoRight, name: jsonData.logoRightFileName || jsonData.startLogoRight?.name },
                  artworkLogoLeft: { ...jsonData.artworkLogoLeft, src: artworkLogoLeft },
                  artworkLogoRight: { ...jsonData.artworkLogoRight, src: artworkLogoRight },
                  license: jsonData.license || config.application.license
                };

                saveStorage(updatedConfig, configParam);
                window.location.reload();
              }
            } catch (error) {
              console.error("Error loading configuration:", error);
            }
          }
        };
      
        fetchData();
      }, []);
      
  

    let stripeWidthOptions = config.application.stripesWidths.map((option: number, i: number) => {
        return (
            <option key={i} value={option}>{option}</option>
        );
    });

    const updateUserActivity = () => {
        setState(pv => {
            const user: User = pv.userLoggedIn;
            user.updateActivity();

            return ({ ...pv, userLoggedIn: user });
        });
    };

    const validateInput = (value: number, min: number, max: number): boolean => {
        if(!isNaN(value) && value >= min && value <= max) {
            return true;
        } else {
            return false;
        }
    };

    const handleInputChange = (propName: string, newValue: number | boolean, deeperPropName?: string) => {
        updateUserActivity();

        setState(pv => {
            const currentConfig: any = pv.tempConfig;

            if(deeperPropName === undefined) {
                currentConfig[propName] = newValue;
            } else {
                currentConfig[propName][deeperPropName] = newValue;
            }
            return ({ ...pv, tempConfig: currentConfig });
        });
    };

    const handleLanguageChange = (language: string) => {
        setState(pv => {
            const currentConfig: any = pv.tempConfig;
            currentConfig['selectedLanguage'] = language;
            return ({ ...pv, tempConfig: currentConfig });
        });
    };

    const handleLicenseReadGenerateFile = async (event: any) => {
        updateUserActivity();

        const allowedFileExtensions = ["key", "keynote"];
        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase();
        
        if (fileExtension && allowedFileExtensions.includes(fileExtension)) {
            const reader = new FileReader();
            reader.readAsText(file);
            let objectUrl = URL.createObjectURL(file);
        
            reader.onload = async function() {
                if (reader.result) {
                    setIsFileLoaded(true);
                    const base64 = await convertFileToBase64(file);
                    setLicenseFileGenerateFile({
                        src: base64,
                        text: reader.result as string,
                        fileName: fileName,
                    });
                } else {
                    setIsFileLoaded(false);
                    setState(pv => ({ ...pv, actionStatus: "License upload failed" }));
                }
                URL.revokeObjectURL(objectUrl);
            };
        } else {
            setIsFileLoaded(false);
            setState(pv => ({ ...pv, actionStatus: "The file format required is .key or .keynote." }));
        }
    };

    const handleActivationFileRead = async (event: any) => {
        updateUserActivity();

        const allowedFileExtensions = ["json"];
        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase();
        
        if (fileExtension && allowedFileExtensions.includes(fileExtension)) {
            const reader = new FileReader();
            reader.readAsText(file);
            let objectUrl = URL.createObjectURL(file);
        
            reader.onload = async function() {
                if (reader.result) {
                    try {
                        const json = JSON.parse(reader.result as string);
                        if (json && json.license && json.license.text) {
                            const updatedConfig = { ...state.tempConfig, license: json.license };
                            saveStorage(updatedConfig, configParam);
                            window.location.reload();
                        }
                    } catch (e) {
                        setState(pv => ({ ...pv, actionStatus: "Activation file format error" }));
                    }
                } else {
                    setState(pv => ({ ...pv, actionStatus: "Activation file upload failed" }));
                }
                URL.revokeObjectURL(objectUrl);
            };
        } else {
            setState(pv => ({ ...pv, actionStatus: "The file format required is .json." }));
        }
    };

    const handleTokenActivation = () => {
        const token = state.pastedToken.trim();
        if (!token) return;

        const isValid = verifyLicense(token);

        if (isValid) {
            const licenseObj = {
                text: token,
                src: "data:text/plain;base64," + btoa(token),
                fileName: "Token Activation"
            };
            const updatedConfig = { ...state.tempConfig, license: licenseObj };
            saveStorage(updatedConfig, configParam);
            window.location.reload();
        } else {
            setState(pv => ({ ...pv, actionStatus: "Invalid token" }));
            alert(t("incorrectLoginDetails"));
        }
    };

    const handleRemoveLicense = () => {
        if (window.confirm(t("removeLicense") + "?")) {
            const updatedConfig = { ...state.tempConfig, license: undefined };
            saveStorage(updatedConfig, configParam);
            window.location.reload();
        }
    };

    const handleGenerateActivationFile = () => {
        if (!licenseFileGenerateFile) return;
        
        generateActivationFile({
            ...state.tempConfig,
            license: {
                src: licenseFileGenerateFile.src,
                text: licenseFileGenerateFile.text,
                fileName: licenseFileGenerateFile.fileName,
            }
        });
    }

    const generateActivationFile = (newConfig: any) => {
        const element = document.createElement("a");
        const textFile = new Blob([JSON.stringify(newConfig)], {type: 'application/json'});
        const fileName: string = !!state.generatedActivationFileName ? `${state.generatedActivationFileName}.json` : "activation.json";

        element.href = URL.createObjectURL(textFile);
        element.download = fileName;
        document.body.appendChild(element); 
        element.click();

        setState(pv => ({ 
            ...pv, 
            generatedActivationFileHref: element.href, 
            generatedActivationFileName: fileName,
        }));
    };

    const handleFileRead = (event: any, side: string) => {
        updateUserActivity();
        
        const allowedFileTypes = ["image/png", "image/jpeg"];
        const file = event.target.files[0];

        if(file && allowedFileTypes.includes(file.type)) {
            let img = new Image();
            let objectUrl = URL.createObjectURL(file);
            img.onload = async function(this: any) {
                if(this.width <= 185 && this.height <= 85) {
                    const base64 = await convertFileToBase64(file);
        
                    setState(pv => {
                        const currentConfig: any = pv.tempConfig;
                        currentConfig[`startLogo${side}`].src = base64;
                        currentConfig[`startLogo${side}`].name = file.name;
                        currentConfig[`artworkLogo${side}`].src = base64;
                        currentConfig[`artworkLogo${side}`].name = file.name;
                        
                        return ({ ...pv, tempConfig: currentConfig, actionStatus: "" });
                    });
                } else {
                    setState(pv => ({ ...pv, actionStatus: "logoDimensionsError" }));
                }
                URL.revokeObjectURL(objectUrl);
            };
            img.src = objectUrl;
        } else {
            setState(pv => ({ ...pv, actionStatus: "fileFormatError" }));
        }
    }

    const logoLeft = require(`./../../assets/img/intro/logo-left.png`);
	const logoRight = require(`./../../assets/img/intro/logo-right.png`);

    return <div className={`settings-pop-up settings-pop-up--${(params.show ? 'show' : 'hide')}`}>
        <div className={`settings-pop-up__login-screen settings-pop-up__login-screen--${state.userLoggedIn.isLogged ? 'hide' : 'show'}`}>
            <input className="settings-pop-up__input settings-pop-up__input--text" type="text" placeholder={t("username")} value={state.username} onChange={(event) => { setState(pv => ({ ...pv, username: event.target.value })) }} />
            <input className="settings-pop-up__input settings-pop-up__input--password" type="password" placeholder={t("password")} value={state.password} onChange={(event) => { setState(pv => ({ ...pv, password: event.target.value })) }} />

            <div className="settings-pop-up__buttons-wrapper">
                <button className="settings-pop-up__button settings-pop-up__button--main" onClick={() => {
                    const result = loginUser(state.username, state.password);
                    if(result.status) {
                        checkUserInterval = setInterval(() => {
                            if(!result.user.isUserLoggedIn()) {
                                clearInterval(checkUserInterval);

                                setState(prevState => {
                                    return ({ ...prevState, actionStatus: "logoutInactive", userLoggedIn: emptyUser, checkUserInterval });
                                });
                            }
                        }, 5000);

                        setState(prevState => {
                            return ({ ...prevState, username: "", password: "", userLoggedIn: result.user, actionStatus: "", checkUserInterval});
                        });
                    } else {
                        setState(prevState => {
                            return ({ ...prevState, password: "", actionStatus: result.textStatus });
                        });
                    }
                }}>{t("signIn")}</button>
                <button className="settings-pop-up__button" onClick={() => { params.onToggleSettingsPopUp(false) }}>{t("cancel")}</button>
            </div>

            <p className="settings-pop-up__action-status">{t(state.actionStatus)}</p>

            {params.inactiveLicenseHideElements && (
                <div className="settings-pop-up__activation-container" style={{marginTop: '30px', borderTop: '1px solid #D1D3D4', paddingTop: '20px'}}>
                    <h2 className="settings-pop-up__activation-title" style={{fontSize: '18px'}}>{t("activationRequired")}</h2>
                    
                    <div className="settings-pop-up__activation-card" style={{boxShadow: 'none', border: '1px solid #E6E7E8'}}>
                        <div style={{marginBottom: '10px'}}>
                            <span className="settings-pop-up__status-badge settings-pop-up__status-badge--inactive">
                                {t("status")}: {t("inactive")}
                            </span>
                        </div>

                        <p style={{color: '#6D6E71', fontSize: '13px', marginBottom: '15px'}}>{t("pleasePasteTokenDescription")}</p>

                        <div className="settings-pop-up__token-area-wrapper">
                            <textarea 
                                className="settings-pop-up__token-textarea"
                                placeholder={t("pasteTokenHere")}
                                value={state.pastedToken}
                                onChange={(e) => setState(pv => ({ ...pv, pastedToken: e.target.value }))}
                            />
                            <button 
                                className="settings-pop-up__button settings-pop-up__button--main" 
                                style={{width: '100%', marginTop: '10px'}}
                                onClick={handleTokenActivation}
                                disabled={!state.pastedToken.trim()}
                            >
                                {t("activateNow")}
                            </button>
                        </div>

                        <div className="settings-pop-up__divider-with-text">
                            <span>{t("orUploadFile")}</span>
                        </div>

                        <div className={`settings-pop-up__settings-row`} style={{flexDirection: 'column', gap: '8px', marginTop: '5px'}}>
                            <span className="settings-pop-up__settings-input-wrapper" style={{width: '100%', justifyContent: 'center'}}>
                                <input type="file" accept=".json" onChange={ e => handleActivationFileRead(e) } />
                            </span>
                            <span className="settings-pop-up__license-format-warning">{t("fileFormatRequiredJson")}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>

        <div className={`settings-pop-up__settings-screen settings-pop-up__settings-screen--${state.userLoggedIn.isLogged ? 'show' : 'hide'}`}>
            {(!params.inactiveLicenseHideElements || state.userLoggedIn.type === 'admin') && (
                <>
                    <div className={`settings-pop-up__settings-screen-content-wrapper`}>
                    <div className={`settings-pop-up__settings-screen--half--left`}>
                        <div className={`settings-pop-up__settings-row ${!['admin','user'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("timer")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input className="settings-pop-up__settings-input" type="number" min="0" max="9999" step="1" value={state.tempConfig.timer} onChange={(event) => { if(validateInput(parseInt(event.target.value), 0, 9999)) handleInputChange("timer", parseInt(event.target.value)) }} />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("maxShapeRotation")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input className="settings-pop-up__settings-input" type="number" min="0" max="359" step="1" value={state.tempConfig.maxShapeRotationDegree} onChange={(event) => { if(validateInput(parseInt(event.target.value), 0, 359)) handleInputChange("maxShapeRotationDegree", parseInt(event.target.value)) }} />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("shapeRotationStepLeft")}:</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input className="settings-pop-up__settings-input" type="number" min="0.05" max="180" step="0.05" value={state.tempConfig.shapeRotationDegree.left} onChange={(event) => { if(validateInput(parseFloat(event.target.value), 0.05, 180)) handleInputChange("shapeRotationDegree", parseFloat(parseFloat(event.target.value).toFixed(2)), "left") }} />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("shapeRotationStepRight")}:</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input className="settings-pop-up__settings-input" type="number" min="0.05" max="180" step="0.05" value={state.tempConfig.shapeRotationDegree.right} onChange={(event) => { if(validateInput(parseFloat(event.target.value), 0.05, 180)) handleInputChange("shapeRotationDegree", parseFloat(parseFloat(event.target.value).toFixed(2)), "right") }} />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("backgroundStripesStartingWidth")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <select className="settings-pop-up__settings-input" defaultValue={config.application.stripesWidth} onChange={(event) => { if(validateInput(parseFloat(event.target.value), 0, 5)) handleInputChange("stripesWidth", parseFloat(parseFloat(event.target.value).toFixed(2))) }}>
                                    {stripeWidthOptions}
                                </select>
                            </span>
                        </div>

                        <span className="settings-pop-up__horizontal-line"></span>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("hideQrCode")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.hideIcon.qrcode} onChange={ event => handleInputChange("hideIcon", event.target.checked, "qrcode") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("hideShareCode")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.hideIcon.share} onChange={ event => handleInputChange("hideIcon", event.target.checked, "share") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("hidePrintCode")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.hideIcon.print} onChange={ event => handleInputChange("hideIcon", event.target.checked, "print") } />
                            </span>
                        </div>

                        <span className="settings-pop-up__horizontal-line"></span>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("changeDefaultLanguage")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <LanguageSwitcher handleLanguageChange={(language: string) => handleLanguageChange(language)} />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("changeLanguage")}:`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <LanguageSwitcher handleLanguageChange={(language: string) => handleLanguageChange(language)} temporaryLanguageStatus={true} />
                            </span>
                        </div>
                    </div>

                    <div className={`settings-pop-up__settings-screen--half`}>
                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("introLogoLeft")}:</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="file" name="file" onChange={ e => handleFileRead(e, "Left") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['user'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <img src={(state.tempConfig.startLogoLeft.src && state.tempConfig.startLogoLeft.src.length > 0) ? state.tempConfig.startLogoLeft.src : ((config.application.startLogoLeft.src && config.application.startLogoLeft.src.length > 0) ? config.application.startLogoLeft.src : (logoLeft.default || logoLeft))}
                                alt={'Start logo left or placeholder'} />
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            {config.application.startLogoLeft.name && <p className="settings-pop-up__settings-label">{`${t("fileName")}:`} <span>{config.application.startLogoLeft.name}</span></p>}
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("hideIntroLogo")}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.startLogoLeft.hide} onChange={ event => handleInputChange("startLogoLeft", event.target.checked, "hide") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("hideArtworkLogo")}`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.artworkLogoLeft.hide} onChange={ event => handleInputChange("artworkLogoLeft", event.target.checked, "hide") } />
                            </span>
                        </div>

                        <span className="settings-pop-up__horizontal-line"></span>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("introLogoRight")}:</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="file" name="file" onChange={ e => handleFileRead(e, "Right") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['user'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <img src={(state.tempConfig.startLogoRight.src && state.tempConfig.startLogoRight.src.length > 0) ? state.tempConfig.startLogoRight.src : ((config.application.startLogoRight.src && config.application.startLogoRight.src.length > 0) ? config.application.startLogoRight.src : (logoRight.default || logoRight))}
                                alt={'Start logo right or placeholder'} />
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            {config.application.startLogoRight.name && <p className="settings-pop-up__settings-label">{`${t("fileName")}:`} <span>{config.application.startLogoRight.name}</span></p>}
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{t("hideIntroLogo")}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.startLogoRight.hide} onChange={ event => handleInputChange("startLogoRight", event.target.checked, "hide") } />
                            </span>
                        </div>

                        <div className={`settings-pop-up__settings-row ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__settings-row--hide' : ''}`}>
                            <p className="settings-pop-up__settings-label">{`${t("hideArtworkLogo")}`}</p>
                            <span className="settings-pop-up__settings-input-wrapper">
                                <input type="checkbox" className="settings-pop-up__settings-input-checkbox" defaultChecked={config.application.artworkLogoRight.hide} onChange={ event => handleInputChange("artworkLogoRight", event.target.checked, "hide") } />
                            </span>
                        </div>

                        <span className="settings-pop-up__horizontal-line"></span>
                    </div>
                </div>

                {state.userLoggedIn.type === 'admin' && (
                    <div className="settings-pop-up__superadmin-panel">
                        <div className="settings-pop-up__admin-controls-grid">
                            <div className="settings-pop-up__admin-column">
                                <h3 className="settings-pop-up__admin-section-title">{t("manageLicense")}</h3>
                                
                                <div className="settings-pop-up__activation-card settings-pop-up__activation-card--admin">
                                    <p className="settings-pop-up__admin-section-description">{t("overwriteLicense")}</p>
                                    <textarea 
                                        className="settings-pop-up__token-textarea settings-pop-up__token-textarea--short"
                                        placeholder={t("pasteTokenHere")}
                                        value={state.pastedToken}
                                        onChange={(e) => setState(pv => ({ ...pv, pastedToken: e.target.value }))}
                                    />
                                    <button 
                                        className="settings-pop-up__button settings-pop-up__button--main settings-pop-up__button--full-width" 
                                        onClick={handleTokenActivation}
                                        disabled={!state.pastedToken.trim()}
                                    >
                                        {t("activateNow")}
                                    </button>

                                    <div className="settings-pop-up__divider-with-text">
                                        <span>OR</span>
                                    </div>

                                    <button 
                                        className="settings-pop-up__button settings-pop-up__button--reset settings-pop-up__button--full-width" 
                                        onClick={handleRemoveLicense}
                                    >
                                        {t("removeLicense")}
                                    </button>
                                </div>
                            </div>

                            <div className="settings-pop-up__admin-column">
                                <h3 className="settings-pop-up__admin-section-title">{t("licenseGenerationTool")} (Admin Only)</h3>
                                
                                <div className="settings-pop-up__activation-card settings-pop-up__activation-card--admin">
                                    <div className="settings-pop-up__settings-row settings-pop-up__settings-row--admin">
                                        <p className="settings-pop-up__settings-label">{t("selectKeyFile")}</p>
                                        <span className="settings-pop-up__settings-input-wrapper settings-pop-up__settings-input-wrapper--left">
                                            <input type="file" accept=".key, .keynote" onChange={ e => handleLicenseReadGenerateFile(e) } />
                                        </span>
                                    </div>

                                    <div className="settings-pop-up__settings-row settings-pop-up__settings-row--admin settings-pop-up__settings-row--gap">
                                        <p className="settings-pop-up__settings-label">{t("fileName")}</p>
                                        <input className="settings-pop-up__settings-input settings-pop-up__settings-input--150-px" type="text" maxLength={15}
                                            value={state.generatedActivationFileName} 
                                            onChange={(event) => { 
                                                let newName: string = event.target.value;
                                                if(typeof newName === 'string' && newName.length <= 15) {
                                                    setState(pv => {
                                                        return ({ ...pv, generatedActivationFileName: newName });
                                                    });
                                                }
                                            }} 
                                        />
                                    </div>

                                    <button className="settings-pop-up__button settings-pop-up__button--admin-action"
                                        disabled={!isFileLoaded} onClick={handleGenerateActivationFile}>
                                        {t("generateAndExportActivationFile")}
                                    </button>

                                    {!!state.generatedActivationFileHref && 
                                        <p style={{display: 'block', marginTop: '10px', fontSize: '12px', color: '#009E33'}}>
                                            {t("success")}! <a href={state.generatedActivationFileHref} download={state.generatedActivationFileName} style={{fontWeight: 'bold', color: '#1074BC'}}>{t("download")} {state.generatedActivationFileName}</a>
                                        </p>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
            )}

            {/* Removal of old activation container placement - merged into login screen for better flow */}


            <div className="settings-pop-up__buttons-wrapper">
                <button className="settings-pop-up__button settings-pop-up__button--main" onClick={() => {
                    saveStorage(state.tempConfig, configParam);
                    clearInterval(checkUserInterval);
                    logoutUser(state.userLoggedIn);
                    params.onToggleSettingsPopUp(false);
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                }}>{t("saveAndRestartApp")}</button>

                <button 
                    className={`settings-pop-up__button settings-pop-up__button--reset ${!['admin'].includes(state.userLoggedIn.type!) ? 'settings-pop-up__button--hide' : ''}`} 
                    onClick={() => { 
                        updateUserActivity();
                        setState(pv => ({ ...pv, tempConfig: {...getDefaultAppConfig()} }));
                    }}
                >
                    {t("resetSettingsToDefault")}
                </button>

                <button className="settings-pop-up__button" onClick={() => {
                    clearInterval(checkUserInterval);
                    logoutUser(state.userLoggedIn);
                    setState(pv => ({ ...pv, userLoggedIn: emptyUser, actionStatus: "" }));
                    params.onToggleSettingsPopUp(false);
                }}>{t("cancelAndSignOut")}</button>
            </div>

            <p className="settings-pop-up__action-status">{t(state.actionStatus)}</p>
        </div>
    </div>;
}